<template>
  <transition name="modal">
    <div class="overlay-bg">
      <div class="overlay">
        <div class="loading-wrapper" v-if="isLoading">
          <LoadingAnimation />
        </div>

        <button class="overlay__close-btn" @click="$emit('close-panel')">
          <span class="overlay__close-btn-text">Close</span>
        </button>

        <div class="share-container">
          <h1>Save Your Package</h1>

          <form
            class="form form--email"
            novalidate
            autocomplete="on"
            @submit.prevent="formSubmit"
          >
            <WarningPanel
              v-if="errorResponse"
              :msg="errorResponse"
              :severe="true"
            />

            <fieldset class="form__fieldset" v-if="!emailSent">
              <div class="form-item">
                <label class="form__label" for="email">
                  <span class="email-label"
                    >Pop in your email address and we’ll send you a link so you
                    can come back to your selected package later</span
                  >
                  <FormError
                    v-if="$v.email.$error && !$v.email.required"
                    msg="Email address required"
                  />
                  <FormError
                    v-if="$v.email.$error && !$v.email.email"
                    msg="Valid email address required"
                  />
                  <FormError
                    v-if="errorResponse"
                    msg="There has been a problem sending the email, try again or note down share link for reference"
                  />
                </label>

                <div class="input-wrapper">
                  <input
                    type="text"
                    name="email"
                    id="email"
                    class="form__input"
                    placeholder="Email"
                    required
                    autocomplete="email"
                    v-model.trim="email"
                  />
                  <button type="submit" class="btn btn--big">Save</button>
                </div>
              </div>
            </fieldset>
            <div class="form-success" v-else>
              <EnvelopeIcon
                width="50px"
                height="38px"
                aria-role="presentation"
              />
              <h4>Saved!</h4>
              <p>
                We’ve saved your package for you and emailed you a link so you
                can come back to it whenever you’re ready. You can also use this
                link to share it with your housemates.
              </p>
            </div>
          </form>
        </div>

        <div class="share-container package-share-wrapper">
          <h1 v-if="!hasNativeShare">Share Your Package</h1>
          <h2 class="share-or" v-else>or</h2>

          <SocialShare
            share-action="Share Package"
            share-action-subtitle="Share this package with your housemates"
            :address-line1="addressLine1"
            :share-link="shareLink"
            share-subject="Unihomes join link for our utilities at "
            :share-body="shareBody"
            :whats-app-share-body="whatsAppShareBody"
            :inline="shouldInlineShare"
            @whatsAppShare="trackWhatsAppShare"
            @emailShare="trackEmailShare"
            @copyShareLink="trackCopyLink"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { email, required } from "vuelidate/lib/validators";
import axiosInstance from "@/utility/axiosInstance";
import FormError from "@/components/forms/FormError";
import SocialShare from "@/components/SocialShare";
import WarningPanel from "@/components/WarningPanel";
import { isSmallDevice } from "@/utility/matchMedia";
import { canUseNativeShare } from "@/utility/nativeShare";
import EnvelopeIcon from "@/assets/svg/icon-envelope.svg";
import LoadingAnimation from "@/components/LoadingAnimation";

export default {
  name: "PackageSaveShareOverlay",
  components: {
    LoadingAnimation,
    EnvelopeIcon,
    FormError,
    WarningPanel,
    SocialShare,
  },
  data() {
    return {
      isLoading: false,
      shareLink: null,
      emailSent: false,
      errorResponse: "",
      shareSubject: "",
      whatsAppShareBody:
        "Check out the bills package I’ve made with UniHomes. They’ll set up and pay our shared bills:\n\n",
    };
  },
  computed: {
    ...mapState(["referral", "discount"]),
    ...mapState({
      addressGuid: (state) => {
        return state.houseInformation.address.guid;
      },
    }),
    ...mapGetters([
      "addressLine1",
      "postcode",
      "housemateCount",
      "selectedProductPriceIds",
      "hasValidDiscount",
      "hasUsedDiscount",
      "selectedBroadbandProductOption",
      "hasAddressGuid",
      "selectedTvLicenceProductOption",
    ]),
    email: {
      get() {
        return this.$store.state.personalDetails.email;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    shouldInlineShare() {
      return !isSmallDevice();
    },
    hasNativeShare() {
      return canUseNativeShare();
    },
    shareBody() {
      return `Hey,

Let’s not faff about or fuss over our shared utilities. UniHomes will set up and pay them for us. We just make one monthly payment each from our own bank accounts. Easy! Check out our bills package here:

${this.shareLink}`;
    },
  },
  methods: {
    trackWhatsAppShare() {
      this.trackGoogleEvent(
        "Click share via WhatsApp",
        "Get Price",
        "Share price WhatsApp",
      );
    },
    trackEmailShare() {
      this.trackGoogleEvent(
        "Click share via email",
        "Get Price",
        "Share price email",
      );
    },
    trackCopyLink() {
      this.trackGoogleEvent("Click Copy", "Get Price", "Copy share price link");
    },
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$store.commit("SHOW_LOADING_PANEL");
        this.errorResponse = "";

        this.trackGoogleEvent(
          "Save Package with Email Address",
          "Get Price",
          "Save Package with Email Address",
        );

        const shareData = {
          email: this.email,
          hm8s: this.housemateCount,
          broadbandOptionCode: this.selectedBroadbandProductOption.code,
          tvLicenceOptionCode: this.selectedTvLicenceProductOption.code,
          selectedProducts: this.selectedProductPriceIds,
        };

        if (this.hasAddressGuid) {
          shareData["addressGuid"] = this.addressGuid;
        } else {
          shareData["postcode"] = this.postcode;
        }

        if (this.referral.isValid) {
          shareData.referral = this.referral.code;
        }

        if (this.hasUsedDiscount) {
          shareData.offerCode = this.discount.offer;
          shareData.affiliateCode = this.discount.aff;
        }

        if (this.$route.query.packageShare) {
          shareData.packageShare = this.$route.query.packageShare;
        }

        axiosInstance
          .post("/email_summary", shareData)
          .then(() => {
            this.emailSent = true;
          })
          .catch((error) => {
            this.emailSent = false;

            if (!error.response) {
              this.errorResponse = "Network error accessing the UniHomes API";
            } else {
              this.errorResponse = error.response.data.messages[0];
            }
          })
          .then(() => {
            this.$store.commit("HIDE_LOADING_PANEL");
          });
      }
    },
    fetchSharePackageLink() {
      this.isLoading = true;

      const shareData = {
        hm8s: this.housemateCount,
        broadbandOptionCode: this.selectedBroadbandProductOption.code,
        tvLicenceOptionCode: this.selectedTvLicenceProductOption.code,
        selectedProducts: this.selectedProductPriceIds,
      };

      if (this.hasAddressGuid) {
        shareData["addressGuid"] = this.addressGuid;
      } else {
        shareData["postcode"] = this.postcode;
      }

      if (this.referral.isValid) {
        shareData.referral = this.referral.code;
      }

      if (this.hasValidDiscount) {
        shareData.voucherCode = this.discount.code;
      }

      if (this.email) {
        shareData.fetchEmail = this.email;
      }

      if (this.$route.query.packageShare) {
        shareData.packageShare = this.$route.query.packageShare;
      }

      axiosInstance
        .post("/share_package", shareData)
        .then(({ data }) => {
          this.shareLink = data;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  created() {
    this.fetchSharePackageLink();
  },
  validations() {
    return {
      email: {
        required,
        email,
      },
    };
  },
};
</script>

<style scoped lang="scss">
.share-container {
  max-width: $container-width;
}

h1 {
  margin-bottom: 1rem;
  font-size: $button-font-size;
}

h3 {
  font-size: 2rem;
  font-weight: $font-regular;
  color: $black;
  margin: 0 0 $spacing-medium;
}

.form--email {
  .form__fieldset {
    margin: 0;
  }

  .input-wrapper {
    border: 2px solid $grey-mid;
    border-radius: $border-radius-xs;

    @include media("<medium") {
      border: none;
    }

    @include media(">=medium") {
      @include inlineform;
    }

    input {
      background: none;
      flex-shrink: 1;

      @include media(">=medium") {
        border: none;
        margin: 0 $spacing-small;
        padding: 0;
      }

      &:focus {
        outline: none;
      }
    }

    button {
      flex-shrink: 0;
      background: $brand-green;
      color: $white;
      padding: $spacing-tiny 0;
      margin: $spacing-tiny $spacing-tiny $spacing-tiny 0;
      width: 18%;
      border: none;

      @include media("<medium") {
        width: 100%;
        margin-top: $spacing-xs;
      }

      @include media(">=medium") {
        min-height: unset;
      }

      &:focus {
        outline: none;
      }
    }
  }
}

.form-success {
  text-align: center;
  color: $white;
  background-color: $brand-green;
  padding: $spacing-medium;
  border-radius: $border-radius-small;

  svg {
    width: 50px;
    height: 38px;
    margin: $spacing-small auto $spacing-medium;
  }

  h4 {
    font-weight: $font-regular;
    margin: 0 0 $spacing-small;
  }

  p {
    font-size: 1.6rem;
    margin: 0;
  }
}

.email-label {
  font-size: 18px;
  display: block;
  margin-bottom: 15px;
  text-align: center;
}

.package-share-wrapper {
  h1 {
    margin-bottom: 0;

    @include media("<medium") {
      margin-bottom: $spacing-medium;
    }
  }

  .share-or {
    margin: $spacing-small 0 0 0;
    color: $grey-darkish;
    font-weight: $font-medium;
  }
}

.loading-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: $brand-blue;

  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
